import {
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { Box, Button, CircularProgress } from '@mui/material';
import ModalComponent from '../../ModalComponent';
import { VideoTypeValue, VideoType } from '../../../utils/video';
import config from '../../../config';
import InputVideo from '../../Form/InputVideo';
import VideoSelectionList from '../../../pages/Master/BlockVideosManagement/VideoSelectionList';
import { VideoContext, VideoInterface } from '../../../contexts/VideoContext';
import ImageModalPaginator from '../ImageModal/ImageModalPaginator';
import { PaginationMetaLink } from '../../../models/Paginator';
import api from '../../../services/api';

interface VideoModalProps {
  currentFilename: string
  editHandler: (video: VideoInterface) => void
}

function VideoModal({ editHandler, currentFilename }: VideoModalProps) {
  const customNavTabs = 'nav navTabs';
  const customTabContent = 'tab-content tabContent';

  const {
    videos,
    createVideo,
  } = useContext(VideoContext);
  const [show, setShow] = useState(false);
  const [currentTab, setCurrentTab] = useState<VideoType | null>(null);
  const [displayVideos, setDisplayVideos] = useState<VideoInterface[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoInterface | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  // errors
  const [videoMaxSizeError, setVideoMaxSizeError] = useState<string | null>(null);

  // Pagination state for each tab
  const [systemVideoPage, setSystemVideoPage] = useState(1);
  const [myVideoPage, setMyVideoPage] = useState(1);
  const [systemVideoLinks, setSystemVideoLinks] = useState<PaginationMetaLink[]>([]);
  const [myVideoLinks, setMyVideoLinks] = useState<PaginationMetaLink[]>([]);
  const [systemVideoTotal, setSystemVideoTotal] = useState(0);
  const [myVideoTotal, setMyVideoTotal] = useState(0);
  const [systemVideoCurrentPage, setSystemVideoCurrentPage] = useState(1);
  const [myVideoCurrentPage, setMyVideoCurrentPage] = useState(1);
  const [systemVideoFromItem, setSystemVideoFromItem] = useState(0);
  const [systemVideoToItem, setSystemVideoToItem] = useState(0);
  const [myVideoFromItem, setMyVideoFromItem] = useState(0);
  const [myVideoToItem, setMyVideoToItem] = useState(0);

  const fetchVideosForTab = useCallback(async (type: VideoType, page?: number) => {
    setIsLoading(true);
    try {
      let targetPage = page;
      if (targetPage === undefined) {
        targetPage = type === VideoTypeValue.SYSTEM_VIDEO ? systemVideoPage : myVideoPage;
      }

      const res = await api.get('/v1/videos', { params: { type, page: targetPage } });

      if (res.data == null) {
        return;
      }

      const result = res.data.data.map((d: VideoInterface) => ({
        id: d.id,
        src: d.src,
        file_name: d.file_name,
        type: d.type,
      }));

      const paginationLinks = res.data.meta?.links || [];
      const paginationTotal = res.data.meta?.total || 0;
      const paginationCurrentPage = res.data.meta?.current_page || 1;
      const paginationFromItem = res.data.meta?.from || 0;
      const paginationToItem = res.data.meta?.to || 0;

      switch (type) {
        case VideoTypeValue.SYSTEM_VIDEO:
          setDisplayVideos(result);
          setSystemVideoLinks(paginationLinks);
          setSystemVideoTotal(paginationTotal);
          setSystemVideoCurrentPage(paginationCurrentPage);
          setSystemVideoFromItem(paginationFromItem);
          setSystemVideoToItem(paginationToItem);
          if (targetPage !== undefined) {
            setSystemVideoPage(targetPage);
          }
          break;
        case VideoTypeValue.MY_VIDEO:
          setDisplayVideos(result);
          setMyVideoLinks(paginationLinks);
          setMyVideoTotal(paginationTotal);
          setMyVideoCurrentPage(paginationCurrentPage);
          setMyVideoFromItem(paginationFromItem);
          setMyVideoToItem(paginationToItem);
          if (targetPage !== undefined) {
            setMyVideoPage(targetPage);
          }
          break;
        default:
      }
    } finally {
      setIsLoading(false);
    }
  }, [systemVideoPage, myVideoPage]);

  const handleVideoUpload = useCallback((video: File) => {
    setIsLoading(true);

    createVideo(
      video,
      currentTab,
      () => {
        setIsLoading(false);
        if (currentTab) {
          const page = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoPage : myVideoPage;
          fetchVideosForTab(currentTab, page);
        }
      },
      () => { setShow(true); },
    );
  }, [createVideo, currentTab, fetchVideosForTab, systemVideoPage, myVideoPage]);

  const handleTabChange = useCallback((type: VideoType) => {
    if (type === VideoTypeValue.SYSTEM_VIDEO) {
      setSystemVideoPage(1);
    } else if (type === VideoTypeValue.MY_VIDEO) {
      setMyVideoPage(1);
    }
    setCurrentTab(type);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    if (!currentTab) return;
    if (currentTab === VideoTypeValue.SYSTEM_VIDEO) {
      setSystemVideoPage(page);
    } else if (currentTab === VideoTypeValue.MY_VIDEO) {
      setMyVideoPage(page);
    }
    fetchVideosForTab(currentTab, page);
  }, [currentTab, fetchVideosForTab]);

  const handleSave = useCallback((video: VideoInterface | null) => {
    if (video === null) return;

    editHandler(video);
    setShow(false);
  }, [editHandler]);

  useEffect(() => {
    if (!show) {
      setCurrentTab(null);
      setSystemVideoPage(1);
      setMyVideoPage(1);
      return;
    }
    setSelectedVideo(null);
    setSystemVideoPage(1);
    setMyVideoPage(1);
    setCurrentTab(VideoTypeValue.SYSTEM_VIDEO);
  }, [show]);

  useEffect(() => {
    if (!show || currentTab === null) {
      return;
    }
    const page = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoPage : myVideoPage;
    fetchVideosForTab(currentTab, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentTab]);

  useEffect(() => {
    const found = videos.find((video) => video.file_name === currentFilename);
    if (found) {
      setSelectedVideo(found);
    }
  }, [currentFilename, videos]);

  const currentLinks = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoLinks : myVideoLinks;
  const currentTotal = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoTotal : myVideoTotal;
  const currentPageNum = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoCurrentPage : myVideoCurrentPage;
  const currentFromItem = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoFromItem : myVideoFromItem;
  const currentToItem = currentTab === VideoTypeValue.SYSTEM_VIDEO ? systemVideoToItem : myVideoToItem;

  const paginatorSlot = currentLinks && currentLinks.length > 0 && currentPageNum && currentTotal > 0 ? (
    <ImageModalPaginator
      links={currentLinks}
      currentPage={currentPageNum}
      onPageChange={handlePageChange}
      total={currentTotal}
      fromItem={currentFromItem}
      toItem={currentToItem}
    />
  ) : null;

  return (
    <>
      <Button
        onClick={(event) => {
          setShow(true);
          event.stopPropagation();
        }}
        variant="contained"
        component="label"
        sx={{
          backgroundColor: '#5555558A',
        }}
      >
        画像を選択する
      </Button>

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <ModalComponent
          size="xl"
          title="動画選択"
          confirmButtonText="選択する"
          onConfirm={() => {
            handleSave(selectedVideo);
            setSelectedVideo(null);
            setVideoMaxSizeError(null);
          }}
          show={show}
          disabled={selectedVideo === null}
          closeModal={() => {
            setVideoMaxSizeError(null);
            setShow(false);
          }}
          centered
          animation={false}
          bodyClassName="m-0 p-0"
          backdropClassName="z-index-2000"
          style={{ zIndex: 2000 }}
          aboveButtonsSlot={paginatorSlot}
        >
          <div className="tab modalTabs">
            <ul className={customNavTabs} role="tablist" style={{ overflow: 'auto' }}>
              <li>
                <a
                  onClick={() => {
                    if (!isLoading) {
                      handleTabChange(VideoTypeValue.SYSTEM_VIDEO);
                    }
                  }}
                  className={currentTab === VideoTypeValue.SYSTEM_VIDEO ? 'active' : ''}
                  href="#tabVideo_1"
                  data-bs-toggle="tab"
                  role="tab"
                  style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}
                >
                  システム動画
                </a>
              </li>
              <li>
                <a
                  onClick={() => {
                    if (!isLoading) {
                      handleTabChange(VideoTypeValue.MY_VIDEO);
                    }
                  }}
                  className={currentTab === VideoTypeValue.MY_VIDEO ? 'active' : ''}
                  href="#tabVideo_2"
                  data-bs-toggle="tab"
                  role="tab"
                  style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}
                >
                  マイ動画
                </a>
              </li>
            </ul>
            <div className={customTabContent}>
              <div id="tabImage_1" className="tab-pane tabPane active">
                <div
                  style={{ display: currentTab === VideoTypeValue.MY_VIDEO ? 'block' : 'none' }}
                  className="imageUpload"
                >
                  <Box>
                    <Button
                      className="btn btn-primary"
                      variant="contained"
                      component="label"
                      disabled={isLoading}
                      sx={{
                        position: 'relative',
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={15} />
                      ) : (
                        <>
                          <InputVideo
                            id="id"
                            type={currentTab}
                            isDisabled={false}
                            fileHandler={(file) => handleVideoUpload(file)}
                            onMaxVideoSizeExceeded={(message: any) => {
                              setVideoMaxSizeError(message);
                            }}
                          />
                          動画をアップロードする ...
                        </>
                      )}
                    </Button>
                    <span style={{ marginLeft: 5 }}>{`※動画最大ファイルサイズは${config.video.maxVideoSizeInMb}MBです。`}</span>
                    {videoMaxSizeError && (
                      <div className="text-danger mt-2">
                        {videoMaxSizeError}
                      </div>
                    )}
                  </Box>
                </div>

                <VideoSelectionList
                  videos={displayVideos}
                  selectedId={selectedVideo?.id}
                  handleSelected={(id) => {
                    setSelectedVideo(displayVideos.find((video) => video.id === id) || null);
                  }}
                />
              </div>
            </div>
          </div>
        </ModalComponent>
      </div>
    </>
  );
}

export default VideoModal;
